/**
 * Contenido (copy) del catálogo de planes del paywall — rediseño de Figma
 * "Pricing" (node 2044:448). Ver ADR-2026-07-28-rediseno-paywall-catalogo-componentes-presentacionales.
 *
 * Es SÓLO presentación: precios, países y descuentos siguen saliendo de la
 * lógica que ya vivía en `Paywall.tsx` (`getPlanPrice` / `formatPrice` /
 * cupón Hot Sale). Acá vive únicamente lo que el diseño muestra como texto.
 *
 * ⚠️ `credits` es un número **de copy**, autoría del diseño: el backend no tiene
 * hoy una noción de "créditos por plan" (la cuota real es `max_fotos_mes`, y
 * `utils/credits.ts` sólo define el costo por acción del editor, donde
 * 1 imagen = 1 crédito). Los conteos de `generates` (imágenes/videos) SÍ son los
 * reales del catálogo (`max_fotos_mes` y los videos por tier). Si el equipo
 * define una cuota de créditos de verdad, este campo debe pasar a derivarse
 * de ella.
 */

/** Card del selector: los 3 planes que muestra el diseño. */
export type PricingPlanKey = 'emprendimiento' | 'negocio' | 'enterprise';

/** Orden de izquierda a derecha en el selector (Básico · Pro · Ultra). */
export const PRICING_PLAN_KEYS: PricingPlanKey[] = [
  'emprendimiento',
  'negocio',
  'enterprise',
];

/** Plan destacado con el badge "Popular" sobre el selector. */
export const RECOMMENDED_PLAN: PricingPlanKey = 'negocio';

export interface PricingPlanContent {
  /** Etiqueta corta del selector segmentado. */
  tab: string;
  /** Título dentro de la card. */
  title: string;
  /** Bajada bajo el título. */
  subtitle: string;
  /**
   * Créditos mensuales que muestra la caja destacada. Ver la advertencia del
   * encabezado: es copy del diseño, no un dato del backend.
   */
  credits: number;
  /** Líneas de "Se pueden generar:" (conteos reales del catálogo). */
  generates: string[];
  /**
   * Línea de herencia ("Todo lo del plan X y..."), con ícono de flecha y en
   * gris. `null` en Básico, que no hereda de nadie.
   */
  inherits: string | null;
  /** Features con check. */
  features: string[];
  /** Label del CTA fijo del pie. */
  cta: string;
}

/**
 * Créditos que consume el video **más barato**. El costo real de un video es por
 * segundo (~1 crédito por segundo del video final), así que 5 es el piso: un video
 * corto. Por eso la línea de videos dice **"hasta"** y la de imágenes no — las
 * imágenes son exactas (1 crédito = 1 imagen entregada) y los videos dependen del
 * largo. Ver ADR-2026-08-03-videos-por-plan-se-calculan-con-el-piso-real.
 */
export const CREDITOS_POR_VIDEO_MIN = 5;

/** Cuántos videos entran en `creditos`, en el mejor caso (todos del largo mínimo). */
export const videosPorCreditos = (creditos: number): number =>
  Math.floor(creditos / CREDITOS_POR_VIDEO_MIN);

/** Línea de imágenes de "Se pueden generar:". Es exacta: 1 crédito = 1 imagen. */
export const lineaImagenes = (creditos: number): string =>
  `${creditos.toLocaleString('es-AR')} imagenes`;

/** Línea de videos de "Se pueden generar:". Es un TECHO, de ahí el "hasta". */
export const lineaVideos = (creditos: number): string =>
  `hasta ${videosPorCreditos(creditos).toLocaleString('es-AR')} videos`;

/**
 * Cuota mensual de créditos por plan (Ultra usa `ULTRA_TIER_CREDITS`, que depende
 * del tier). Existe como constante para que `credits` y las líneas de "Se pueden
 * generar:" salgan del MISMO número: cuando estaban sueltos, la cuota decía 250 y
 * los videos seguían calculados sobre otra cifra (ADR-2026-08-03-videos-por-plan-se-calculan-con-el-piso-real).
 */
const CREDITOS_PLAN = { emprendimiento: 60, negocio: 250, enterprise: 500 } as const;

export const PRICING_CONTENT: Record<PricingPlanKey, PricingPlanContent> = {
  emprendimiento: {
    tab: 'Básico',
    title: 'Básico',
    subtitle: 'Para pequeños emprendimientos',
    credits: CREDITOS_PLAN.emprendimiento,
    // Sin línea de videos a propósito: la generación de videos arranca en Pro (es una
    // feature del plan, no una cuestión de saldo).
    generates: [lineaImagenes(CREDITOS_PLAN.emprendimiento)],
    inherits: null,
    features: [
      'Edición de imágenes',
      'Acceso desde computadora, tablet y celular',
      'Soporte prioritario',
      'Conexión con Tienda Nube',
    ],
    cta: 'Suscribirme al Básico',
  },
  negocio: {
    tab: 'Pro',
    title: 'Pro',
    subtitle: 'Ideal para negocios en crecimiento',
    // 250 = `company_profiles.max_fotos_mes` real del plan `negocio` (101 empresas
    // activas con ese valor). Mostraba 300, que no lo otorga nadie
    // (ADR-2026-08-03-creditos-por-plan-mienten).
    credits: CREDITOS_PLAN.negocio,
    generates: [
      lineaImagenes(CREDITOS_PLAN.negocio),
      lineaVideos(CREDITOS_PLAN.negocio),
    ],
    inherits: 'Todo lo del plan Básico y...',
    features: [
      'Generación de videos',
      'Creación y edición de video avanzada',
      'Creación de fotos catálogos',
      'Creación de multi marcas',
    ],
    cta: 'Suscribirme al Pro',
  },
  enterprise: {
    tab: 'Ultra',
    title: 'Ultra',
    subtitle: 'Para producción de imagenes y gestión de catálogos grandes',
    // Ultra es multi-tier: `credits` y `generates` de acá son el fallback del
    // primer tier; la card los pisa con los del tier elegido en el slider
    // (`ULTRA_TIER_CREDITS`).
    credits: CREDITOS_PLAN.enterprise,
    generates: [
      lineaImagenes(CREDITOS_PLAN.enterprise),
      lineaVideos(CREDITOS_PLAN.enterprise),
    ],
    inherits: 'Todo lo del plan Pro y...',
    features: [
      'Capacitación personalizada',
      'Acceso al instante de los últimos modelos',
    ],
    cta: 'Suscribirme al Ultra',
  },
};

/**
 * Créditos por tier de Ultra, en el mismo orden que `ULTRA_TIERS` de `pricingPlans`.
 *
 * **Son exactamente la cuota de imágenes del tier**, porque 1 crédito = 1 imagen
 * entregada (el backend debita por output: ver `expectedDeliveredCount` +
 * `debitDelivered`). Antes decían `[1000, 2000, 4000]` —el doble de lo que otorga
 * `max_fotos_mes` en cada plan (`enterprise1` 500, `enterprise2` 1000)— porque se
 * asumió que crédito e imagen eran unidades distintas.
 * Ver ADR-2026-08-03-creditos-por-plan-mienten.
 */
export const ULTRA_TIER_CREDITS = [500, 1000, 2000];

/**
 * Créditos EXTRA del primer mes al darse de alta desde gratis (`activationCredits`
 * del servidor de pagos). Son un plus por única vez: el mes 1 se acredita
 * `cuota + bonus` y de ahí en adelante la cuota normal.
 *
 * Números del servidor de pagos (2026-08-03):
 *   emprendimiento 60 → 90 (+30) · negocio 250 → 300 (+50)
 *   enterprise1 500 → 600 · enterprise2 1000 → 1100 · enterprise3 2000 → 2100 (+100)
 *   enterprise legacy 900 → 1000 (+100)
 *
 * La caja de "créditos por mes" NO los incluye a propósito: muestra la cuota
 * recurrente, que es con la que el usuario va a vivir
 * (ADR-2026-08-03-creditos-por-plan-mienten).
 */
export const CREDITOS_BIENVENIDA: Record<PricingPlanKey, number> = {
  emprendimiento: 30,
  negocio: 50,
  enterprise: 100,
};

/** Etiqueta corta de cada tope del slider de Ultra (imágenes por mes). */
export const ULTRA_TIER_LABELS = ['500', '1000', '2000'];

/** Descuento que muestra el badge del toggle Anual (visual, ADR-2026-07-28-switch-mensual-anual-visual-solo). */
export const ANNUAL_DISCOUNT_LABEL = '20% OFF';

/**
 * ⚠️ PLACEHOLDER (ADR-2026-07-28-switch-mensual-anual-visual-solo). No hay plan anual en el backend: este porcentaje sólo
 * existe para poder dibujar la opción "Anual" del selector de facturación que ven
 * las cuentas `dev_tester`. Cuando el plan anual exista de verdad, el precio tiene
 * que venir del catálogo/proveedor, NO calcularse acá.
 */
export const ANNUAL_DISCOUNT_RATE = 0.2;
